// Deploy/runtime API — extracted from api.js (Tier 2 #9a, refactor).
// SSE streaming endpoints (streamDeployApp, streamStartApp) bypass the axios
// instance because they need direct fetch() + ReadableStream access.

import api from './api';
import { getAccessToken } from './auth';
import { runtimeEnv } from './runtimeEnv';

export const deployApp = async (appId) => {
  const response = await api.post(`/dashboard/apps/${appId}/deploy`, {});
  return response.data;
};

/**
 * Stream deployment with real-time SSE progress events.
 * @param {string|number} appId
 * @param {(event: {type: string, label: string, detail?: string}) => void} onEvent
 * @returns {Promise<void>} resolves on "complete", rejects on "error"
 */
export const streamDeployApp = (appId, onEvent) => {
  const token = getAccessToken();
  const baseURL = runtimeEnv('REACT_APP_API_BASE_URL', '');

  return new Promise((resolve, reject) => {
    fetch(`${baseURL}/api/dashboard/apps/${appId}/deploy/stream`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: '{}',
    }).then(response => {
      if (!response.ok) {
        return response.json().then(err => {
          // Platform-block 403s carry an extra `reason` field — append it so
          // the operator's message ("Maintenance window…") reaches the user.
          const base = err.error || 'Deploy failed';
          const msg = err.reason ? `${base}: ${err.reason}` : base;
          throw new Error(msg);
        });
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      const processChunk = ({ done, value }) => {
        if (done) {
          resolve();
          return;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop(); // keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const event = JSON.parse(line.slice(6));
              onEvent(event);
              if (event.type === 'complete') {
                resolve();
                return;
              }
              if (event.type === 'error') {
                // Prefer `detail` — it carries the readable, actionable message
                // (lint sentences + fix suggestions). `label` is just the step
                // name ("Linting app"). Cancel the reader so the stream closes
                // immediately instead of dangling after we reject.
                const err = new Error(event.detail || event.label || 'Deployment failed');
                err.label = event.label;
                try { reader.cancel(); } catch (_) { /* already closing */ }
                reject(err);
                return;
              }
            } catch (e) {
              // skip malformed event
            }
          }
        }

        reader.read().then(processChunk).catch(reject);
      };

      reader.read().then(processChunk).catch(reject);
    }).catch(reject);
  });
};

export const fetchDeployPreview = async (appId) => {
  const response = await api.get(`/dashboard/apps/${appId}/deploy/preview`);
  return response.data;
};

// SSE stream for starting an app (creates K8s pod + deploys config)
export const streamStartApp = (appId, onEvent) => {
  const token = getAccessToken();
  const baseURL = runtimeEnv('REACT_APP_API_BASE_URL', '');

  return new Promise((resolve, reject) => {
    fetch(`${baseURL}/api/dashboard/apps/${appId}/start/stream`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: '{}',
    }).then(response => {
      if (!response.ok) {
        return response.json().then(err => {
          const base = err.error || 'Start failed';
          const msg = err.reason ? `${base}: ${err.reason}` : base;
          throw new Error(msg);
        });
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      const processChunk = ({ done, value }) => {
        if (done) { resolve(); return; }
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const event = JSON.parse(line.slice(6));
              onEvent(event);
              if (event.type === 'complete') { resolve(); return; }
              if (event.type === 'error') {
                const err = new Error(event.detail || event.label || 'Start failed');
                err.label = event.label;
                try { reader.cancel(); } catch (_) { /* already closing */ }
                reject(err);
                return;
              }
            } catch (e) { /* skip */ }
          }
        }
        reader.read().then(processChunk).catch(reject);
      };
      reader.read().then(processChunk).catch(reject);
    }).catch(reject);
  });
};

export const stopApp = async (appId) => {
  const response = await api.post(`/dashboard/apps/${appId}/stop`);
  return response.data;
};

export const fetchRuntimeStatus = async (appId) => {
  const response = await api.get(`/apps/${appId}/runtime-status`);
  return response.data;
};

export const fetchDeploymentHistory = async (appId, page = 1, limit = 10) => {
  const response = await api.get(`/dashboard/apps/${appId}/deployments`, { params: { page, limit } });
  return response.data;
};

// Fetch the app-wide structured data-collection API docs (all deployed
// workflows + app-level sections). Serialized to markdown client-side.
export const fetchAppDataApiDocs = async (appId) => {
  const response = await api.get(`/apps/${appId}/data-api-docs`);
  return response.data;
};

// Generate a plain-language Software Requirements Document (Markdown) from the
// app's deployed config. The backend narrates it via the LLM gateway (with a
// deterministic structured fallback). Returns { markdown }. LLM narration can
// take several seconds, so callers should show a pending state.
export const fetchAppRequirementsDoc = async (appId) => {
  const response = await api.get(`/apps/${appId}/requirements-doc`, { timeout: 180000 });
  return response.data;
};

// Read-only overview of every workflow that's currently in the deployed
// appconfig (public.tbl_appconfig_workflows). Returns each workflow's content
// reshaped into the same FullWorkflowConfig shape the editor Overview tab uses,
// so the frontend can run it through transformWorkflow() without further
// translation. Unrelated to the live editor heads.
export const fetchAppDeployedOverview = async (appId) => {
  const response = await api.get(`/apps/${appId}/deployed-overview`);
  return response.data;
};

// Drill-down behind an `aiEmployee` node on the deployed diagram. Reads the
// deployed tier only (aiemployee.tbl_appconfig_ai_employees + the deployed
// agentic-tool twin), so it shows the config that is actually RUNNING rather
// than the draft the editor's own drill-down reads.
// `ref` carries whatever the node has: {userId, id, name, email}.
// A 404 with not_published:true means the employee was never deployed.
export const fetchDeployedAIEmployee = async (appId, ref = {}) => {
  const params = {};
  if (ref.userId) params.user_id = ref.userId;
  if (ref.id != null && ref.id !== '') params.id = ref.id;
  if (ref.email) params.email = ref.email;
  if (ref.name) params.name = ref.name;
  const response = await api.get(`/apps/${appId}/deployed-ai-employee`, { params });
  return response.data;
};

// Same, for an `aiAgent` node: the deployed agent twin plus its deployed tools.
// `ref` is {agentId, name, type}; type is an optional probe hint.
export const fetchDeployedAgent = async (appId, ref = {}) => {
  const params = {};
  if (ref.agentId != null && ref.agentId !== '') params.agent_id = ref.agentId;
  if (ref.name) params.name = ref.name;
  if (ref.type) params.type = ref.type;
  const response = await api.get(`/apps/${appId}/deployed-agent`, { params });
  return response.data;
};
